import { type UserUsageSummary } from '@/hooks/useAdminUsers'
import {
  LogIn, ShieldAlert, Key, ClipboardList,
  Globe, CheckCircle, Clock, Zap,
  Puzzle, RotateCw, Bell,
  MessageCircle, Brain, Lightbulb,
} from 'lucide-react'

interface StatCardProps {
  label: string
  value: string | number
  icon: React.ElementType
  color?: string
}

function StatCard({ label, value, icon: Icon, color = 'copper' }: StatCardProps) {
  const colorMap: Record<string, { bg: string; iconColor: string }> = {
    copper:  { bg: 'bg-copper-50', iconColor: 'text-copper-500' },
    sky:     { bg: 'bg-sky-50', iconColor: 'text-sky-500' },
    emerald: { bg: 'bg-emerald-50', iconColor: 'text-emerald-500' },
    purple:  { bg: 'bg-purple-50', iconColor: 'text-purple-500' },
    amber:   { bg: 'bg-amber-50', iconColor: 'text-amber-500' },
    teal:    { bg: 'bg-teal-50', iconColor: 'text-teal-500' },
    red:     { bg: 'bg-red-50', iconColor: 'text-red-500' },
  }
  const c = colorMap[color] || colorMap.copper

  return (
    <div className="flex items-center gap-3 p-4 rounded-xl border border-warm-200 bg-white">
      <div className={`w-10 h-10 rounded-lg ${c.bg} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-5 h-5 ${c.iconColor}`} />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-heading font-bold text-midnight-800 leading-tight">{value}</p>
        <p className="text-xs text-warm-500 truncate">{label}</p>
      </div>
    </div>
  )
}

interface UsageSummaryCardsProps {
  usage: UserUsageSummary
  userRole: string
}

export default function UsageSummaryCards({ usage, userRole }: UsageSummaryCardsProps) {
  const fmt = (n: number | null | undefined) => n != null ? n.toLocaleString() : '\u2014'
  const pct = (n: number | null | undefined) => n != null ? `${(n * 100).toFixed(1)}%` : '\u2014'
  const ms = (n: number | null | undefined) => n != null ? `${n}ms` : '\u2014'

  return (
    <div className="space-y-4">
      {/* Auth stats — all users */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Logins (30d)" value={fmt(usage.login_count_30d)} icon={LogIn} color="copper" />
        <StatCard label="Failed Logins (30d)" value={fmt(usage.failed_login_count_30d)} icon={ShieldAlert} color="red" />
        <StatCard label="Features Granted" value={fmt(usage.feature_count)} icon={Key} color="purple" />
        <StatCard label="Admin Actions (30d)" value={fmt(usage.audit_actions_30d)} icon={ClipboardList} color="sky" />
      </div>

      {/* Tenant admin stats */}
      {userRole === 'tenant_admin' && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="API Requests (30d)" value={fmt(usage.api_requests_30d)} icon={Globe} color="sky" />
          <StatCard label="Success Rate" value={pct(usage.api_success_rate)} icon={CheckCircle} color="emerald" />
          <StatCard label="Avg Response" value={ms(usage.avg_response_time_ms)} icon={Clock} color="amber" />
          <StatCard label="Engage Sessions (30d)" value={fmt(usage.engage_sessions_30d)} icon={Zap} color="copper" />
        </div>
      )}

      {/* Consumer stats */}
      {userRole === 'consumer' && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <StatCard label="Utterances (30d)" value={fmt(usage.consumer_utterances_30d)} icon={MessageCircle} color="teal" />
          <StatCard label="Distillations (30d)" value={fmt(usage.consumer_distillations_30d)} icon={Brain} color="purple" />
          <StatCard label="Insights (30d)" value={fmt(usage.consumer_insights_30d)} icon={Lightbulb} color="amber" />
        </div>
      )}

      {/* Extension stats */}
      {(usage.extension_sessions_30d != null && usage.extension_sessions_30d > 0) && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <StatCard label="Extension Sessions (30d)" value={fmt(usage.extension_sessions_30d)} icon={Puzzle} color="sky" />
          <StatCard label="Turns (30d)" value={fmt(usage.extension_turns_30d)} icon={RotateCw} color="copper" />
          <StatCard label="Nudges (30d)" value={fmt(usage.extension_nudges_30d)} icon={Bell} color="purple" />
        </div>
      )}
    </div>
  )
}
